import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Loader2, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (link) link.setAttribute('href', '/admin-manifest.json');
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, null, {
        params: { email, password }
      });
      
      const token = response.data.access_token;
      // Triple storage: localStorage + cookie + sessionStorage
      localStorage.setItem('admin_token', token);
      localStorage.setItem('admin_last_login', new Date().toISOString());
      document.cookie = `admin_token=${token}; path=/; max-age=${30*24*60*60}; SameSite=Lax; Secure`;
      toast.success('Connexion admin réussie');
      navigate('/admin');
    } catch (error) {
      console.error('Admin login error:', error);
      toast.error(error.response?.data?.detail || 'Identifiants invalides');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src="/logo-youmi.png" alt="YOUMI GREEN VTC" className="w-16 h-16" />
          <div>
            <span className="font-bold text-2xl font-['Outfit'] text-white">YOUMI GREEN</span>
            <span className="text-emerald-500 font-bold text-2xl font-['Outfit'] ml-1">VTC</span>
          </div>
        </div>

        {/* Admin Badge */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Shield className="w-5 h-5 text-emerald-500" />
          <span className="text-emerald-500 font-medium uppercase tracking-wider text-sm">Administration</span>
        </div>

        {/* Login Form */}
        <div className="glass-card rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white font-['Outfit'] mb-6 text-center">
            Connexion Admin
          </h2>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Mail className="w-4 h-4 text-emerald-500" />
                Email
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@youmigreenvtc.com"
                className="bg-zinc-800 border-zinc-700 text-white"
                required
                data-testid="admin-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Lock className="w-4 h-4 text-emerald-500" />
                Mot de passe
              </Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Votre mot de passe"
                className="bg-zinc-800 border-zinc-700 text-white"
                required
                data-testid="admin-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full btn-primary"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Connexion...
                </>
              ) : (
                'Se connecter'
              )}
            </Button>
          </form>

          {/* Forgot Password Link */}
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => navigate('/admin/reset-password')}
              className="text-red-400 hover:text-red-300 text-sm"
            >
              Mot de passe oublié ?
            </button>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-zinc-400 hover:text-white text-sm"
            >
              Retour au site
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
